import { z } from 'zod';
import { localizedString } from './text';
import { semanticQuerySchema } from './semantic';

export const apiBindingSchema = z.object({
  source: z.literal('api'),
  /** Request path; `{a.b}` placeholders are interpolated from the screen context. */
  path: z.string().min(1),
  method: z.enum(['GET', 'POST']).optional(),
  body: z.unknown().optional(),
  query: z.record(z.string(), z.string()).optional(),
  /** Dot path selecting a part of the response (`content[0].status`). */
  select: z.string().optional(),
});
export const contextBindingSchema = z.object({ source: z.literal('context'), path: z.string().min(1) });
export const queryBindingSchema = z.object({ source: z.literal('query'), query: semanticQuerySchema });
export const staticBindingSchema = z.object({ source: z.literal('static'), value: z.unknown() });

export const bindingSchema = z.discriminatedUnion('source', [apiBindingSchema, contextBindingSchema, queryBindingSchema, staticBindingSchema]);

export type ApiBinding = z.infer<typeof apiBindingSchema>;
export type ContextBinding = z.infer<typeof contextBindingSchema>;
export type QueryBinding = z.infer<typeof queryBindingSchema>;
export type StaticBinding = z.infer<typeof staticBindingSchema>;
export type Binding = z.infer<typeof bindingSchema>;

export const visibleWhenSchema = z.object({
  /** Dot path into the screen context. */
  fact: z.string().min(1),
  op: z.enum(['eq', 'ne', 'in', 'notNull', 'isNull']),
  value: z.unknown().optional(),
});
export type VisibleWhen = z.infer<typeof visibleWhenSchema>;

export const screenComponentSchema = z.object({
  id: z.string().min(1),
  /** Registry name of the design-system component. */
  component: z.string().min(1),
  props: z.record(z.string(), z.unknown()).optional(),
  /** Prop name → binding. */
  bindings: z.record(z.string(), bindingSchema).optional(),
  permission: z.string().optional(),
  visibleWhen: visibleWhenSchema.optional(),
  /** Slot (prop) name → child component ids rendered into it. */
  slots: z.record(z.string(), z.array(z.string())).optional(),
});
export type ScreenComponent = z.infer<typeof screenComponentSchema>;

export type LayoutType = 'stack' | 'grid' | 'split';

export interface LayoutDefinition {
  type: LayoutType;
  /** Number (px) or any CSS length. */
  gap?: number | string;
  /** Grid column count. */
  columns?: number;
  /** Stack direction (default column). */
  direction?: 'row' | 'column';
  /** Component ids or nested layouts. */
  children: Array<string | LayoutDefinition>;
}

export const layoutSchema: z.ZodType<LayoutDefinition> = z.lazy(() =>
  z.object({
    type: z.enum(['stack', 'grid', 'split']),
    gap: z.union([z.number(), z.string()]).optional(),
    columns: z.number().int().positive().optional(),
    direction: z.enum(['row', 'column']).optional(),
    children: z.array(z.union([z.string(), layoutSchema])),
  }),
);

function collectLayoutIds(layout: LayoutDefinition, out: string[] = []): string[] {
  for (const child of layout.children) {
    if (typeof child === 'string') out.push(child);
    else collectLayoutIds(child, out);
  }
  return out;
}

export const screenDefinitionSchema = z
  .object({
    key: z.string().min(1),
    title: localizedString.optional(),
    /** Extension slot id this screen is published for (e.g. `inbound.receipt.detail.sidebar`). */
    slot: z.string().optional(),
    layout: layoutSchema,
    components: z.array(screenComponentSchema),
  })
  .superRefine((def, ctx) => {
    const ids = new Set<string>();
    def.components.forEach((c, index) => {
      if (ids.has(c.id)) ctx.addIssue({ code: 'custom', path: ['components', index, 'id'], message: `Duplicate component id "${c.id}"` });
      ids.add(c.id);
    });
    for (const id of collectLayoutIds(def.layout)) {
      if (!ids.has(id)) ctx.addIssue({ code: 'custom', path: ['layout'], message: `Layout references unknown component "${id}"` });
    }
    def.components.forEach((c, index) => {
      for (const [slot, children] of Object.entries(c.slots ?? {})) {
        for (const child of children) {
          if (!ids.has(child)) ctx.addIssue({ code: 'custom', path: ['components', index, 'slots', slot], message: `Slot references unknown component "${child}"` });
        }
      }
    });
  });
export type ScreenDefinition = z.infer<typeof screenDefinitionSchema>;
